import { CandlestickChart } from 'lucide-react'
import { motion } from 'motion/react'

import { Skeleton } from '@/components/ui'
import { cn } from '@/utils'

interface SecuritiesPendingStateProps {
  // 'panel' is the full Portfolio tab empty state (roomy, with a skeleton preview of the table);
  // 'card' is the compact Overview card row.
  layout: 'panel' | 'card'
  detail: string
}

// A scan line sweeping the icon, to show a document being read. Reused by both pending spots.
const ScanningIcon = ({ className }: { className?: string }) => (
  <div
    className={cn(
      'relative grid place-items-center overflow-hidden rounded-lg bg-primary/10 text-primary',
      className,
    )}
  >
    <CandlestickChart className="size-1/2" strokeWidth={1.75} />
    <motion.div
      aria-hidden
      className="absolute inset-x-0 h-1/3 bg-linear-to-b from-transparent via-primary/25 to-transparent"
      initial={{ top: '-40%' }}
      animate={{ top: '110%' }}
      transition={{
        duration: 1.8,
        ease: 'easeInOut',
        repeat: Number.POSITIVE_INFINITY,
        repeatDelay: 0.4,
      }}
    />
  </div>
)

// Three dots that rise and fall in sequence, the same "working…" beat used in chat.
const PulsingDots = () => (
  <span className="inline-flex items-center gap-1" aria-hidden>
    {[0, 1, 2].map((index) => (
      <span
        key={index}
        className="size-1 rounded-full bg-primary/70 animate-typing-dot"
        style={{ animationDelay: `${index * 0.16}s` }}
      />
    ))}
  </span>
)

export const SecuritiesPendingState = ({
  layout,
  detail,
}: SecuritiesPendingStateProps) => {
  if (layout === 'card') {
    return (
      <div className="flex items-center gap-3 py-1" aria-live="polite">
        <ScanningIcon className="size-9 shrink-0" />
        <div className="min-w-0 space-y-0.5">
          <p className="flex items-center gap-1.5 text-sm font-medium text-foreground">
            Verifying the brokerage statements
            <PulsingDots />
          </p>
          <p className="text-sm text-ink-subtle">{detail}</p>
        </div>
      </div>
    )
  }

  return (
    <motion.div
      className="flex flex-col items-center gap-5 rounded-lg border bg-white p-10 text-center"
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      aria-live="polite"
    >
      <ScanningIcon className="size-14" />
      <div className="space-y-1">
        <p className="flex items-center justify-center gap-2 text-base font-medium text-foreground">
          Verifying the brokerage statements
          <PulsingDots />
        </p>
        <p className="mx-auto max-w-sm text-sm text-ink-subtle">{detail}</p>
      </div>
      {/* A faint preview of the holdings table assembling, so the space reads as "filling in". */}
      <div className="w-full max-w-md space-y-2 pt-2">
        {[0, 1, 2].map((row) => (
          <div key={row} className="flex items-center gap-3">
            <Skeleton className="h-4 flex-1" />
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-20" />
          </div>
        ))}
      </div>
    </motion.div>
  )
}
